import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type { CapabilityTemplateDto } from '../../domain/adapterService';
import { useCapabilityTemplates } from '../../hooks/useAdapterServices';

type Props = {
  open: boolean;
  serviceId: string;
  onClose: () => void;
  onApply: (template: CapabilityTemplateDto | null) => void;
};

export function AddCapabilityDialog({ open, serviceId, onClose, onApply }: Props) {
  const { data: templates = [], isLoading } = useCapabilityTemplates(serviceId, open);
  const [mode, setMode] = useState<'template' | 'blank'>('template');
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    if (!open) return;
    setMode('template');
    setSelectedIdx(0);
  }, [open]);

  const registry = templates.filter(t => t.source === 'registry');
  const existing = templates.filter(t => t.source === 'existing');
  const list = mode === 'template' ? [...registry, ...existing] : [];

  const handleApply = () => {
    if (mode === 'blank') {
      onApply(null);
      onClose();
      return;
    }
    const t = list[selectedIdx];
    if (t) onApply(t);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add capability</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Source</InputLabel>
            <Select
              label="Source"
              value={mode}
              onChange={e => {
                setMode(e.target.value as 'template' | 'blank');
                setSelectedIdx(0);
              }}
            >
              <MenuItem value="template">From template or existing</MenuItem>
              <MenuItem value="blank">Blank new capability</MenuItem>
            </Select>
          </FormControl>
          {mode === 'template' && (
            <>
              {isLoading && <Typography variant="body2">Loading templates…</Typography>}
              {!isLoading && list.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No registry or existing templates. Use blank new capability.
                </Typography>
              )}
              <List dense>
                {list.map((t, i) => (
                  <ListItemButton
                    key={`${t.source}-${t.capability}-${i}`}
                    selected={selectedIdx === i}
                    onClick={() => setSelectedIdx(i)}
                  >
                    <ListItemText
                      primary={t.label}
                      secondary={`${t.capability} · ${t.source}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleApply}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
